import { CommonModule } from "@angular/common";
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Input, OnDestroy, OnInit, Renderer2, ViewChild } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { Observable } from "rxjs";
import { IIndicator } from "@gtmhub/error-handling";
import { LocalizationModule } from "@webapp/localization/localization.module";
import { NoSearchItemsFoundComponent } from "@webapp/search/global-search/components/no-search-items-found/no-search-items-found.component";
import { SearchResultsComponent } from "@webapp/search/global-search/components/search-results/search-results.component";
import { GlobalSearchResult, ITEMS_LIMIT_FOR_SIMPLE_SEARCH, SearchFacetsOptionsEnum } from "@webapp/search/models/search.models";
import { GlobalSearchMediatorService } from "@webapp/search/services/global-search.mediator.service";
import { UiIconModule } from "@webapp/ui/icon/icon.module";
import { UiInputModule } from "@webapp/ui/input/input.module";
import { UiPopoverModule } from "@webapp/ui/popover/popover.module";
import { UiSkeletonModule } from "@webapp/ui/skeleton/skeleton.module";

@UntilDestroy()
@Component({
  selector: "simple-search",
  templateUrl: "./simple-search.component.html",
  styleUrls: ["./simple-search.component.less"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    UiInputModule,
    UiSkeletonModule,
    UiIconModule,
    LocalizationModule,
    FormsModule,
    UiPopoverModule,
    SearchResultsComponent,
    NoSearchItemsFoundComponent,
  ],
})
export class SimpleSearchComponent implements OnInit, OnDestroy {
  @Input()
  public searchPlaceholder: string = "";

  @Input()
  public searchCollections: string;

  public searchTerm: string;
  public shouldShowPopover = false;
  public searchResults: Observable<GlobalSearchResult[]>;
  public gettingSearchData: IIndicator = null;

  private clickSubscription: () => void;
  @ViewChild("searchInput") private searchInput: ElementRef;
  @ViewChild("resultsPopover") private resultsPopover: ElementRef;

  constructor(private globalSearchMediatorService: GlobalSearchMediatorService, private cdr: ChangeDetectorRef, private renderer: Renderer2) {
    this.globalSearchMediatorService.setStaticFacetOptions(
      [SearchFacetsOptionsEnum.Teams, SearchFacetsOptionsEnum.Employees, SearchFacetsOptionsEnum.Roles],
      ITEMS_LIMIT_FOR_SIMPLE_SEARCH
    );

    this.searchResults = this.globalSearchMediatorService.searchResults$;
  }

  public ngOnDestroy(): void {
    if (this.clickSubscription) this.clickSubscription();
  }

  public ngOnInit(): void {
    this.globalSearchMediatorService.indicators$.pipe(untilDestroyed(this)).subscribe((indicators) => {
      this.gettingSearchData = indicators.gettingSearchData;
      this.cdr.detectChanges();
    });
  }

  public onSearchTermChange(): void {
    const skipLoading = this.searchTerm.length < 1;
    this.globalSearchMediatorService.setSearchTerm(this.searchTerm, { skipLoading });
    this.shouldShowPopover = true;

    if (!this.clickSubscription) {
      this.subscribeForClickOutside();
    }

    this.cdr.markForCheck();
  }

  public onEsc(): void {
    this.shouldShowPopover = false;
    this.searchInput.nativeElement.focus();
    this.cdr.markForCheck();
  }

  public focusSearchItems(): void {
    this.resultsPopover?.nativeElement?.querySelector("li.item-row.section-list")?.focus();
  }

  private subscribeForClickOutside(): void {
    this.clickSubscription = this.renderer.listen("window", "click", (e: Event) => {
      if (this.shouldShowPopover && !this.searchInput.nativeElement.contains(e.target) && !this.resultsPopover?.nativeElement.contains(e.target)) {
        this.shouldShowPopover = false;
        this.clickSubscription();
        this.clickSubscription = null;
        this.cdr.detectChanges();
      }
    });
  }
}
